import type { Conversation, Message } from '../../api/chat';

// ─── Constants ────────────────────────────────────────────────────────────────

export const EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '🎉', '🔥', '👀', '✅', '🙏'];
export const PRIORITIES = ['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'];
export const BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function fmtTime(iso: string) {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export function fmtDate(iso: string) {
  const d = new Date(iso);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' });
}

// Conversation-row timestamp: WhatsApp-style.
//   today      → "16:35"
//   yesterday  → "Yesterday"
//   this week  → "Mon", "Tue", …
//   older      → "18/05/25"
export function fmtConvTime(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';

  const now = new Date();
  const startOfDay = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const msPerDay = 24 * 60 * 60 * 1000;
  const daysDiff = Math.floor((startOfDay(now) - startOfDay(d)) / msPerDay);

  if (daysDiff <= 0) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  if (daysDiff === 1) return 'Yesterday';
  if (daysDiff < 7) return d.toLocaleDateString([], { weekday: 'short' });
  return d.toLocaleDateString([], { day: '2-digit', month: '2-digit', year: '2-digit' });
}

// Conversation sort key. The backend's `last_activity` column is only
// bumped by a subset of message-insert paths (send_message + a few system
// flows), so it can lag behind the actual latest message — polls,
// meet_invites, forwards and some system messages create a ChatMessage but
// never update `last_activity`. The row UI shows `last_message.created_at`
// in those cases, so sorting on `last_activity` alone produces conversations
// whose displayed time is newer than every neighbour above them. Take the
// max across all three timestamps so the sort key matches what the row
// actually shows.
export function convSortTime(c: { last_message?: { created_at?: string | null } | null; last_activity?: string | null; created_at?: string | null }): number {
  const toMs = (s?: string | null) => {
    if (!s) return 0;
    const t = new Date(s).getTime();
    return isNaN(t) ? 0 : t;
  };
  return Math.max(
    toMs(c.last_message?.created_at),
    toMs(c.last_activity),
    toMs(c.created_at),
  );
}

export function fmtFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Returns the other participant of a direct message. IDs from the auth JWT and
 * conversation endpoints can be serialised as different primitive types, so
 * compare their canonical string values before selecting the peer.
 */
export function getDmPeer(c: Conversation, userId: string | number | null | undefined) {
  if (c.type !== 'dm') return undefined;
  const currentUserId = userId == null ? '' : String(userId);
  return c.members?.find((member) => String(member.user_id) !== currentUserId);
}

export function convDisplayName(c: Conversation, userId: string | number): string {
  if (c.type !== 'dm') return c.name || 'Group Chat';
  const other = getDmPeer(c, userId);
  const name = other?.name || '';
  if (!name) return 'Direct Message';
  if (name.includes('@')) return name.split('@')[0];
  return name;
}

export function getInitials(name: string): string {
  if (!name) return '?';
  return name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();
}

// Forest Sage palette — warm, earthy, restrained.
// Each pair pairs a saturated edge with a deeper tone for the gradient,
// keeping the overall feel harmonious with the forest-green primary.
export const AVATAR_COLORS = [
  'from-emerald-500 to-emerald-700',
  'from-teal-500 to-teal-700',
  'from-amber-500 to-orange-600',
  'from-rose-400 to-rose-600',
  'from-stone-500 to-stone-700',
  'from-lime-500 to-green-700',
  'from-sky-600 to-cyan-800',
  'from-violet-500 to-violet-700',
];

export function getAvatarColor(name: string): string {
  const code = (name || '?').charCodeAt(0) || 0;
  return AVATAR_COLORS[code % AVATAR_COLORS.length];
}

export function isEditableNow(createdAt: string): boolean {
  return Date.now() - new Date(createdAt).getTime() < 15 * 60 * 1000;
}

export type PendingMessage = Message & { _pending?: boolean };

// ─── Message grouping (Slack-style) ──────────────────────────────────────────

export type GroupedItem =
  | { kind: 'date'; key: string; label: string }
  | { kind: 'message'; msg: PendingMessage; isFirstInGroup: boolean; isLastInGroup: boolean };

const GROUP_WINDOW_MS = 5 * 60 * 1000; // 5 minutes

function isStandalone(msg: PendingMessage): boolean {
  return msg.message_type === 'system' || msg.message_type === 'task_link';
}

function startsNewGroup(prev: PendingMessage, curr: PendingMessage): boolean {
  if (isStandalone(prev) || isStandalone(curr)) return true;
  if (prev.sender_id !== curr.sender_id) return true;
  const gap = new Date(curr.created_at).getTime() - new Date(prev.created_at).getTime();
  if (gap > GROUP_WINDOW_MS) return true;
  return false;
}

export function groupMessages(msgs: PendingMessage[]): GroupedItem[] {
  const out: GroupedItem[] = [];
  if (!msgs || msgs.length === 0) return out;

  // Pass 1: build an array describing each message's group boundaries + date divider placement.
  type Entry = { msg: PendingMessage; isFirstInGroup: boolean; isLastInGroup: boolean; showDate: boolean; dateLabel: string };
  const entries: Entry[] = [];

  let lastDateLabel = '';
  for (let i = 0; i < msgs.length; i++) {
    const curr = msgs[i];
    const prev = i > 0 ? msgs[i - 1] : null;
    const dateLabel = fmtDate(curr.created_at);
    const showDate = dateLabel !== lastDateLabel;
    lastDateLabel = dateLabel;

    // A date boundary also forces a new group.
    const newGroup = !prev || showDate || startsNewGroup(prev, curr);

    entries.push({
      msg: curr,
      isFirstInGroup: newGroup,
      isLastInGroup: false, // fill in pass 2
      showDate,
      dateLabel,
    });
  }

  // Pass 2: a message is "last in group" if the next message starts a new group (or there is no next).
  for (let i = 0; i < entries.length; i++) {
    const next = i + 1 < entries.length ? entries[i + 1] : null;
    entries[i].isLastInGroup = !next || next.isFirstInGroup;
  }

  // Pass 3: emit grouped items with date dividers interspersed.
  for (const e of entries) {
    if (e.showDate) {
      out.push({ kind: 'date', key: `date-${e.dateLabel}-${e.msg.id}`, label: e.dateLabel });
    }
    out.push({ kind: 'message', msg: e.msg, isFirstInGroup: e.isFirstInGroup, isLastInGroup: e.isLastInGroup });
  }

  return out;
}
